// packages/ui/src/components/FileSelectButton/FileSelectButton.tsx

import { forwardRef, useRef } from 'react';
import type { ChangeEvent } from 'react';

import { Button, type ButtonProps } from '../Button/Button';

export interface FileSelectButtonProps extends Omit<ButtonProps, 'onSelect' | 'type'> {
  /** Filtro do seletor nativo, ex.: `'.csv,.xlsx'`. */
  accept?: string;
  /** Permite escolher mais de um arquivo. */
  multiple?: boolean;
  /** Recebe o primeiro arquivo escolhido. */
  onSelect: (file: File) => void;
  /** Recebe todos os arquivos quando `multiple`. */
  onSelectMany?: (files: File[]) => void;
  /** testId aplicado ao input nativo (o botão usa `data-testid` normal). */
  inputTestId?: string;
}

/**
 * Botão que abre o seletor de arquivos do sistema.
 *
 * Para anexo de entidade (S3 + tabela `attachments`) use `AttachmentManager` /
 * `AttachmentUploadField`. Este componente é para o caso em que o arquivo vai
 * cru num endpoint multipart de domínio — import de CSV/XLSX, por exemplo — e
 * nunca vira um attachment.
 *
 * O input é resetado a cada escolha, então escolher o MESMO arquivo duas vezes
 * seguidas dispara `onSelect` nas duas.
 */
export const FileSelectButton = forwardRef<HTMLButtonElement, FileSelectButtonProps>(
  function FileSelectButton(
    { accept, multiple = false, onSelect, onSelectMany, inputTestId, disabled, children, ...rest },
    ref,
  ) {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      event.target.value = '';
      if (files.length === 0) return;
      onSelectMany?.(files);
      if (files[0]) onSelect(files[0]);
    };

    return (
      <>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          className="hidden"
          onChange={handleChange}
          data-testid={inputTestId}
          tabIndex={-1}
          aria-hidden="true"
        />
        <Button
          ref={ref}
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          {...rest}
        >
          {children}
        </Button>
      </>
    );
  },
);
